// src/screens/LoginOtp.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../auth/AuthProvider";
import { apiFetch } from "../api";
import { useSettings } from "../settings"; // ✅ ADD (para update do selectedWalletId)

function cleanOtp(v) {
  return String(v || "").replace(/\D/g, "").slice(0, 6);
}

function OtpSlots({ value, length = 6, activeIndex = 0, disabled }) {
  const v = cleanOtp(value);
  const arr = Array.from({ length }, (_, i) => v[i] || "");

  return (
    <div
      style={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: `repeat(${length}, 1fr)`,
        gap: 10,
        userSelect: "none",
        boxSizing: "border-box",
        opacity: disabled ? 0.7 : 1,
      }}
      aria-label="PIN"
    >
      {arr.map((ch, i) => {
        const isActive = i === Math.min(activeIndex, length - 1);
        const isFilled = !!ch;

        return (
          <div
            key={i}
            style={{
              height: 52,
              borderRadius: 12,
              border: isActive
                ? "1px solid rgba(255,255,255,0.45)"
                : "1px solid rgba(255,255,255,0.14)",
              background: "rgba(0,0,0,0.20)",
              boxShadow: isActive
                ? "0 0 0 4px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.06)"
                : "inset 0 1px 0 rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              fontWeight: 900,
              color: "white",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            {ch ? ch : ""}

            {/* caret discreto */}
            {!isFilled && isActive && !disabled ? (
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  width: 18,
                  height: 2,
                  background: "rgba(255,255,255,0.75)",
                  borderRadius: 2,
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function toInt(x) {
  const n = Number(x);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function normalizeWalletIds(walletids) {
  const arr = Array.isArray(walletids) ? walletids : [];
  const out = [];
  for (const v of arr) {
    const id = toInt(v);
    if (id && id > 0) out.push(id);
  }
  return Array.from(new Set(out));
}

export default function LoginOtp() {
  const nav = useNavigate();
  const loc = useLocation();
  const { verifyPin, requestPin } = useAuth();

  const { update, settings } = useSettings(); // ✅ ADD (settings para não sobrescrever)
  const selectedWalletId = settings?.selectedWalletId ?? null;

  const emailFromState = loc.state?.email || "";
  const [email] = useState(String(emailFromState || "").trim().toLowerCase());

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [focused, setFocused] = useState(false);

  const inputRef = useRef(null);
  const loadingRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (!email) nav("/login", { replace: true });
  }, [email, nav]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus?.(), 120);
    return () => clearTimeout(t);
  }, []);

  const otpClean = useMemo(() => cleanOtp(otp), [otp]);
  const canSubmit = useMemo(() => otpClean.length === 6 && !loading, [otpClean, loading]);

  const doVerify = useCallback(
    async (pin) => {
      if (loadingRef.current) return;

      if (aliveRef.current) setErr(null);

      const v = cleanOtp(pin);
      if (v.length !== 6) {
        if (aliveRef.current) setErr("PIN inválido. Digite os 6 números.");
        return;
      }

      if (aliveRef.current) setLoading(true);

      try {
        // ✅ CHAMA UMA VEZ SÓ (usa o PIN LIMPO v)
        const out = await verifyPin(email, v);

        // ✅ carteira default do usuário vem de walletids[0]
        // - não sobrescreve se já existir selectedWalletId
        // - mas se selectedWalletId existe e não está em walletids, corrige
        const walletids = normalizeWalletIds(out?.walletids);
        const w0 = walletids.length ? walletids[0] : toInt(out?.defaultWalletId);

        const curSel = toInt(selectedWalletId);
        const curSelValid = curSel && curSel > 0;

        const curSelBelongs =
          curSelValid && walletids.length ? walletids.includes(curSel) : curSelValid;

        if (!curSelValid && w0) {
          update({ selectedWalletId: w0 });
        } else if (curSelValid && walletids.length && !curSelBelongs && w0) {
          // carteira salva não pertence mais ao usuário (troca de conta, limpeza, etc.)
          update({ selectedWalletId: w0 });
        }

        // ✅ decide onboarding
        try {
          const s = await apiFetch("/api/onboarding/status");
          if (!aliveRef.current) return;

          if (!s?.accepted) nav("/onboarding/terms", { replace: true });
          else nav("/today", { replace: true });
        } catch {
          if (!aliveRef.current) return;
          nav("/onboarding/terms", { replace: true });
        }
      } catch (e2) {
        if (!aliveRef.current) return;
        setErr(String(e2?.message || "Falha ao verificar PIN"));
      } finally {
        if (!aliveRef.current) return;
        setLoading(false);
      }
    },
    [email, nav, verifyPin, update, selectedWalletId]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      await doVerify(otpClean);
    },
    [doVerify, otpClean]
  );

  // ✅ auto-submit ao completar 6
  useEffect(() => {
    if (otpClean.length === 6 && !loadingRef.current) {
      const t = setTimeout(() => doVerify(otpClean), 80);
      return () => clearTimeout(t);
    }
  }, [otpClean, doVerify]);

  const onResend = useCallback(async () => {
    if (sending || loading) return;
    if (aliveRef.current) setErr(null);
    setSending(true);

    try {
      await requestPin(email);
      if (!aliveRef.current) return;

      setOtp("");
      const t = setTimeout(() => inputRef.current?.focus?.(), 80);
      return () => clearTimeout(t);
    } catch (e2) {
      if (!aliveRef.current) return;
      setErr(String(e2?.message || "Falha ao reenviar PIN"));
    } finally {
      if (!aliveRef.current) return;
      setSending(false);
    }
  }, [sending, loading, requestPin, email]);

  const S = styles;
  const activeIndex = Math.min(otpClean.length, 5);

  return (
    <div style={S.page}>
      {/* background */}
      <div style={S.bgNoise} />
      <div style={S.bgGlow1} />
      <div style={S.bgGlow2} />

      <div style={S.shell}>
        <div style={S.card}>
          <div style={S.cardHighlight} />

          {/* Top row: back */}
          <button
            onClick={() => nav("/login", { replace: true })}
            style={S.backBtn}
            disabled={loading}
          >
            <FiArrowLeft /> Voltar
          </button>

          {/* Brand block */}
          <div style={S.brandRow}>
            <div style={S.brandIconWrap}>
              <img src="/logo-clear.png" alt="FinBot" style={S.brandIcon} draggable={false} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={S.brandName}>FinBot</div>
              <div style={S.brandTagline}>Verificação de acesso</div>
            </div>
          </div>

          <div style={S.header}>
            <div style={S.title}>Verificar PIN</div>
            <div style={S.subtitle}>
              Digite o PIN enviado para{" "}
              <b style={S.emailText} title={email}>
                {email}
              </b>
              .
            </div>
          </div>

          <form onSubmit={onSubmit} style={S.form}>
            <label style={S.label}>PIN</label>

            {/* OTP block (grid) + input invisível */}
            <div
              style={{
                ...S.otpWrap,
                ...(focused ? S.otpWrapFocused : null),
                ...(err ? S.otpWrapError : null),
              }}
              onClick={() => inputRef.current?.focus?.()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") inputRef.current?.focus?.();
              }}
            >
              <OtpSlots value={otpClean} length={6} activeIndex={activeIndex} disabled={loading} />

              <input
                ref={inputRef}
                value={otpClean}
                onChange={(e) => setOtp(cleanOtp(e.target.value))}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label="PIN"
                style={S.hiddenOtpInput}
                disabled={loading}
              />
            </div>

            {err ? (
              <div style={S.error}>{err}</div>
            ) : (
              <div style={S.helper}>Se não chegar, reenvie o PIN.</div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                ...S.primaryBtn,
                ...(canSubmit ? S.primaryBtnEnabled : S.primaryBtnDisabled),
              }}
            >
              <span style={S.primaryBtnText}>{loading ? "Verificando..." : "Entrar"}</span>
              {!loading ? <FiCheck /> : null}
            </button>

            <button
              type="button"
              onClick={onResend}
              disabled={sending || loading}
              style={{
                ...S.secondaryBtn,
                opacity: sending || loading ? 0.7 : 1,
                cursor: sending || loading ? "not-allowed" : "pointer",
              }}
            >
              <span>{sending ? "Reenviando..." : "Reenviar PIN"}</span>
              {!sending ? <FiRefreshCw /> : null}
            </button>
          </form>

          <div style={S.disclaimer}>
            Dica: no iPhone, o PIN pode aparecer como sugestão acima do teclado.
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100dvh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    padding: 16,
    paddingTop: "max(18px, env(safe-area-inset-top))",
    paddingBottom: "max(18px, env(safe-area-inset-bottom))",
    boxSizing: "border-box",
    background:
      "radial-gradient(1200px 700px at 15% -10%, rgba(255,255,255,0.10), rgba(0,0,0,0) 55%), radial-gradient(900px 600px at 85% 120%, rgba(255,255,255,0.07), rgba(0,0,0,0) 60%)",
  },

  bgNoise: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(rgba(255,255,255,0.02), rgba(255,255,255,0.02))",
    opacity: 0.35,
    pointerEvents: "none",
  },
  bgGlow1: {
    position: "absolute",
    left: "-260px",
    top: "-240px",
    width: 620,
    height: 620,
    borderRadius: 9999,
    background: "radial-gradient(circle, rgba(255,255,255,0.10), rgba(0,0,0,0) 60%)",
    filter: "blur(1px)",
    pointerEvents: "none",
  },
  bgGlow2: {
    position: "absolute",
    right: "-260px",
    bottom: "-260px",
    width: 700,
    height: 700,
    borderRadius: 9999,
    background: "radial-gradient(circle, rgba(255,255,255,0.08), rgba(0,0,0,0) 62%)",
    filter: "blur(1px)",
    pointerEvents: "none",
  },

  shell: {
    minHeight:
      "calc(100dvh - max(18px, env(safe-area-inset-top)) - max(18px, env(safe-area-inset-bottom)))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1,
  },

  card: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 22,
    padding: 18,
    boxSizing: "border-box",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
    boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    position: "relative",
    overflow: "hidden",
  },

  cardHighlight: {
    position: "absolute",
    inset: "-40% -20% auto -20%",
    height: 220,
    background: "radial-gradient(closest-side, rgba(255,255,255,0.10), rgba(0,0,0,0))",
    pointerEvents: "none",
  },

  backBtn: {
    background: "transparent",
    border: "none",
    color: "rgba(255,255,255,0.82)",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: 0,
    cursor: "pointer",
    marginBottom: 12,
    position: "relative",
    zIndex: 1,
  },

  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    position: "relative",
    zIndex: 1,
    marginBottom: 12,
  },
  brandIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  },
  brandIcon: { width: 22, height: 22, opacity: 0.95 },

  brandName: { fontSize: 16, fontWeight: 900, letterSpacing: 0.2 },
  brandTagline: { marginTop: 2, fontSize: 13, opacity: 0.70, lineHeight: 1.2 },

  header: { position: "relative", zIndex: 1, marginBottom: 14 },
  title: { fontSize: 22, fontWeight: 900, marginBottom: 6, letterSpacing: 0.2 },
  subtitle: {
    opacity: 0.78,
    lineHeight: 1.35,
    fontSize: 14,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  emailText: { overflowWrap: "anywhere", wordBreak: "break-word" },

  form: { width: "100%", boxSizing: "border-box", position: "relative", zIndex: 1 },
  label: { display: "block", marginBottom: 8, opacity: 0.85, fontSize: 13 },

  otpWrap: {
    position: "relative",
    width: "100%",
    borderRadius: 16,
    padding: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.18)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
    boxSizing: "border-box",
    cursor: "text",
    outline: "none",
  },
  otpWrapFocused: {
    border: "1px solid rgba(255,255,255,0.26)",
    boxShadow: "0 0 0 4px rgba(255,255,255,0.06), inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  otpWrapError: {
    border: "1px solid rgba(255,107,107,0.60)",
    boxShadow: "0 0 0 4px rgba(255,107,107,0.10)",
  },

  hiddenOtpInput: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    opacity: 0.001,
    border: "none",
    background: "transparent",
    color: "transparent",
    caretColor: "transparent",
    outline: "none",
    boxSizing: "border-box",
  },

  error: { marginTop: 10, color: "#ff6b6b", fontSize: 13 },
  helper: { marginTop: 10, opacity: 0.65, fontSize: 13 },

  primaryBtn: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 16,
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    userSelect: "none",
    transition: "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
  },
  primaryBtnEnabled: {
    background: "linear-gradient(180deg, rgba(255,255,255,1), rgba(230,230,230,1))",
    color: "black",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 14px 34px rgba(0,0,0,0.35)",
  },
  primaryBtnDisabled: {
    background: "rgba(255,255,255,0.18)",
    color: "rgba(255,255,255,0.70)",
    fontWeight: 900,
    cursor: "not-allowed",
    boxShadow: "none",
    opacity: 0.70,
  },
  primaryBtnText: { lineHeight: 1 },

  secondaryBtn: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 10,
    padding: 13,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(0,0,0,0.20)",
    color: "white",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },

  disclaimer: {
    marginTop: 12,
    fontSize: 12,
    lineHeight: 1.35,
    opacity: 0.55,
    position: "relative",
    zIndex: 1,
  },
};
